import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, viewportConfig } from '@/lib/animations';
import { servicesData, type ServiceData } from '@/data/servicesData';
import { ServiceModal } from './ServiceModal';

export function ServicesGrid() {
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null);

  return (
    <section id="services" className="py-12 sm:py-14 md:py-16 lg:py-20 bg-gray-50">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
        className="container mx-auto px-5 sm:px-6"
      >
        {/* Section Header */}
        <motion.div variants={fadeInUp} className="text-center mb-10 md:mb-14">
          <span className="text-teal font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
            Our Services
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy-deep tracking-tight mb-4">
            Capabilities Built for{' '}
            <span className="text-teal">Results</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Full-service digital advertising powered by premium broadcast inventory.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {servicesData.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                whileHover={{ y: -8, boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.15)" }}
                onClick={() => setSelectedService(service)}
                className="group relative bg-white rounded-xl p-6 border border-gray-100 cursor-pointer transition-all duration-300 hover:border-orange/30"
              >
                {/* Orange left border accent on hover */}
                <div className="absolute left-0 top-4 bottom-4 w-1 bg-orange rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="p-3 bg-navy-deep/5 rounded-xl w-fit mb-4 group-hover:bg-orange/10 transition-colors duration-300">
                  <IconComponent className="w-6 h-6 text-navy-deep group-hover:text-orange transition-colors duration-300" />
                </div>
                
                <h3 className="text-lg font-semibold text-navy-deep mb-2 group-hover:text-navy transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {service.shortDescription}
                </p>
                
                <div className="flex items-center text-orange text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Learn More
                  <ArrowRight className="ml-1 w-4 h-4" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Service Detail Modal */}
      <ServiceModal 
        isOpen={!!selectedService} 
        onClose={() => setSelectedService(null)} 
        service={selectedService}
      />
    </section>
  );
}
